import { useEffect } from 'react';

const CHATBASE_ID = 'k8INjptaT209w7FGeRRfB';
const CHATBASE_SELECTOR =
  '[id*="chatbase"], [class*="chatbase"], iframe[src*="chatbase"]';
const CHATBASE_WINDOW_SELECTOR =
  '[id*="chatbase-bubble-window"], [class*="chatbase-bubble-window"], [class*="chatbase-window"]';
const CHATBASE_GREETING_SELECTOR =
  '[class*="chatbase-bubble-message"], [class*="chatbase-tooltip"], [id*="chatbase-bubble-message"], [id*="chatbase-tooltip"]';

const isWebviewMode = () => {
  const webviewParam = new URLSearchParams(window.location.search).get(
    'webview'
  );
  return ['1', 'true', 'yes'].includes(String(webviewParam).toLowerCase());
};

const setWidgetVisibility = (visible) => {
  const chatbaseElements = document.querySelectorAll(CHATBASE_SELECTOR);
  chatbaseElements.forEach((el) => {
    el.style.display = visible ? '' : 'none';
  });
};

const collapseChatWindow = () => {
  try {
    if (window.chatbase) {
      window.chatbase('close');
    }
  } catch (error) {
    // Ignore API errors and fallback to DOM-based collapse.
  }

  const expandedPanels = document.querySelectorAll(CHATBASE_WINDOW_SELECTOR);
  expandedPanels.forEach((el) => {
    el.style.display = 'none';
  });
};

const hideChatbaseGreetingMessage = () => {
  const candidates = document.querySelectorAll(CHATBASE_GREETING_SELECTOR);
  candidates.forEach((el) => {
    el.style.display = 'none';
  });

  // Fallback for nodes that don't expose stable class/id names.
  const allChatbaseNodes = document.querySelectorAll(CHATBASE_SELECTOR);
  allChatbaseNodes.forEach((node) => {
    const text = (node.textContent || '').trim().toLowerCase();
    if (text.includes('hi! what can i help you with?')) {
      node.style.display = 'none';
    }
  });

  // Fallback for cross-origin iframes where text cannot be inspected.
  // Keep the circular launcher visible, hide only teaser-like surfaces.
  const chatbaseIframes = document.querySelectorAll('iframe[src*="chatbase"]');
  chatbaseIframes.forEach((iframe) => {
    const rect = iframe.getBoundingClientRect();
    const isTeaserLike =
      rect.width >= 120 &&
      rect.height >= 30 &&
      rect.height <= 140 &&
      window.innerHeight - rect.bottom < 220;

    if (isTeaserLike) {
      iframe.style.display = 'none';
    }
  });
};

export const forceHideChatbaseWidget = () => {
  collapseChatWindow();
  setWidgetVisibility(false);

  window.setTimeout(() => {
    collapseChatWindow();
    setWidgetVisibility(false);
  }, 150);
};

const ChatbaseWidget = ({ isAuthenticated = false }) => {
  // Carga e inicializacion del script solo una vez.
  useEffect(() => {
    if (!isAuthenticated) {
      forceHideChatbaseWidget();
      return;
    }

    if (!window.chatbase || window.chatbase('getState') !== 'initialized') {
      window.chatbase = (...args) => {
        if (!window.chatbase.q) {
          window.chatbase.q = [];
        }
        window.chatbase.q.push(args);
      };
      window.chatbase = new Proxy(window.chatbase, {
        get(target, prop) {
          if (prop === 'q') {
            return target.q;
          }
          return (...args) => target(prop, ...args);
        }
      });
    }

    const loadScript = () => {
      if (document.getElementById(CHATBASE_ID)) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://www.chatbase.co/embed.min.js';
      script.id = CHATBASE_ID;
      script.setAttribute('chatbotId', CHATBASE_ID);
      script.setAttribute('domain', 'www.chatbase.co');
      script.defer = true;
      document.body.appendChild(script);
    };

    if (document.readyState === 'complete') {
      loadScript();
      return;
    }

    window.addEventListener('load', loadScript);
    return () => window.removeEventListener('load', loadScript);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      forceHideChatbaseWidget();
      return;
    }

    setWidgetVisibility(true);
    collapseChatWindow();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isWebviewMode()) {
      return;
    }

    hideChatbaseGreetingMessage();

    const observer = new MutationObserver(() => {
      hideChatbaseGreetingMessage();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Some widget updates happen without DOM node insertions.
    const periodicCleanup = window.setInterval(() => {
      hideChatbaseGreetingMessage();
    }, 400);

    return () => {
      observer.disconnect();
      window.clearInterval(periodicCleanup);
    };
  }, [isAuthenticated]);

  useEffect(
    () => () => {
      forceHideChatbaseWidget();
    },
    []
  );

  // Componente invisible - el widget se inyecta en el DOM
  return null;
};

export default ChatbaseWidget;
